/* SyncHealthPill — WP Mgmt dashboard at-a-glance OL↔WP sync status.
 *
 * Composes two backend views into one popover so operators see both
 * halves of the sync picture without leaving the page:
 *
 *   1. /api/ol-canonical/_sync-health/{org}  — replication_queue tones
 *      (OL → WP outbox state) over the last hour.
 *   2. /api/wp-sync/{org}/status              — per-entity pull
 *      watermarks (WP → OL freshness) sourced from
 *      `db.wp_sync_watermarks` populated by `services.wp_sync_agent`'s
 *      15-minute cron loop.
 *
 * Pill tone (unchanged):
 *   🟢 green  — no dead-letter rows; OL outbox happy
 *   🟡 amber  — at least one row in pending/in_flight
 *   🔴 red    — dead-letter rows (always actionable)
 *
 * Popover now also surfaces:
 *   • Per-entity "Last pulled X ago" for every registered WP projection,
 *     drift counts when the agent saw WP overwrite OL or vice-versa.
 *   • Two action buttons:
 *       "Pull from WP"    — incremental, watermark-aware (fast / cheap)
 *       "Full refresh"    — discards watermarks for one cycle
 *     Both call `POST /api/wp-sync/{org}/pull` which routes through
 *     `pull_org()` in the existing agent — no duplicate cron.
 *
 * Updated Session 24 (May 12, 2026): single source of truth for grid
 * freshness is now the wp_sync_agent + its watermarks. Earlier ad-hoc
 * resync scripts were retired in favour of the agent's `full_refresh`.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  CheckCircle2, Loader2, AlertTriangle, RefreshCw, Download, FastForward, Database, UploadCloud, Eraser,
} from 'lucide-react';
import { onEditSaved } from './wpSyncEvents';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30000;
// Mirrors services/wp_sync_agent.py:_CRON_INTERVAL_SEC. Kept as a constant
// so we can show "Next auto-sweep in Xm" without hard-coding 900 in
// multiple places.
const CRON_INTERVAL_MS = 15 * 60 * 1000;

const TONE_STYLES = {
  green: {
    pill: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/25',
    Icon: CheckCircle2,
    dot:  'bg-emerald-400',
  },
  amber: {
    pill: 'bg-amber-500/15 text-amber-200 border border-amber-500/40 hover:bg-amber-500/25',
    Icon: Loader2,
    dot:  'bg-amber-400 animate-pulse',
  },
  red: {
    pill: 'bg-red-500/15 text-red-300 border border-red-500/50 hover:bg-red-500/25 ring-1 ring-red-500/30',
    Icon: AlertTriangle,
    dot:  'bg-red-400 animate-pulse',
  },
};

/* Compact "X ago" formatter — handles ISO strings, missing values,
   and far-future drift safely. Resolution caps at "days" because
   any watermark older than a few days is clearly stale. */
function timeAgo(iso) {
  if (!iso) return 'never';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 30) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(t).toLocaleDateString();
}

/* Friendly display name for each registry key. Single map so backend
   key renames don't ripple into the UI labels. */
const ENTITY_LABELS = {
  coaches: 'Staff',
  positions: 'Staff Positions',
  staff_groups: 'Staff Groups',
  teams: 'Teams',
  venues: 'Venues',
  games: 'Games',
  schedules: 'Schedules',
  sports: 'Sports',
  players: 'Players',
  sponsors: 'Sponsors',
};

export default function SyncHealthPill({ orgId }) {
  const [health, setHealth] = useState(null);
  const [agent, setAgent] = useState(null);     // /wp-sync/{org}/status
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [pulling, setPulling] = useState(null); // 'incr' | 'full' | null
  const [pullResult, setPullResult] = useState(null);
  const [pushing, setPushing] = useState(false); // OL→WP force resync in flight
  const [pushResult, setPushResult] = useState(null);
  const [clearing, setClearing] = useState(false); // clear stuck errors in flight
  const [clearResult, setClearResult] = useState(null);
  // Immediate-amber state: when DataTable / modal saves a row we
  // pessimistically flip the pill to amber + accelerate polling until
  // the outbox drains. This avoids the up-to-30s lag between user
  // action and the user seeing the sync indicator move.
  const [recentEdits, setRecentEdits] = useState(0);
  const [lastEditAt, setLastEditAt] = useState(0);

  const fetchAll = useCallback(async (opts = {}) => {
    if (!orgId) return;
    if (!opts.silent) setLoading(true);
    try {
      const [h, a] = await Promise.allSettled([
        axios.get(`${API}/api/ol-canonical/_sync-health/${orgId}`),
        axios.get(`${API}/api/wp-sync/${orgId}/status`, { withCredentials: true }),
      ]);
      if (h.status === 'fulfilled') setHealth(h.value.data);
      if (a.status === 'fulfilled') setAgent(a.value.data);
      // We tolerate a 403 on /wp-sync/status (e.g. parent-role viewer) —
      // the queue pill above still renders. Only flag a connectivity
      // failure if BOTH endpoints rejected.
      if (h.status === 'rejected' && a.status === 'rejected') {
        setErr(h.reason?.message || 'Sync probe failed');
      } else {
        setErr(null);
      }
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchAll();
    const t = setInterval(() => fetchAll({ silent: true }), POLL_MS);
    return () => clearInterval(t);
  }, [fetchAll]);

  // Event-driven sync amplification: each successful inline-save /
  // modal-save / bulk-delete fires `ol:edit-saved`. We bump a counter
  // (so we can render "Syncing N edit(s)…"), record the timestamp, and
  // poll faster for ~30s so the user sees the green-amber-green
  // transition without waiting for the next 30s tick.
  useEffect(() => {
    const off = onEditSaved((detail) => {
      const n = detail?.count || 1;
      setRecentEdits((c) => c + n);
      setLastEditAt(Date.now());
      // Kick a fresh fetch immediately so the queue counts move.
      fetchAll({ silent: true });
    });
    return off;
  }, [fetchAll]);

  // Fast-poll for 30s after each edit so the amber→green transition is
  // visible. Cap at 30s of intensified polling, then resume the normal
  // 30s cadence. Bottom banner clears when both the queue is empty AND
  // we're past the cool-down window (recentEdits is reset there too).
  useEffect(() => {
    if (!lastEditAt) return;
    const fastTimer = setInterval(() => fetchAll({ silent: true }), 4000);
    const settle = setTimeout(() => {
      clearInterval(fastTimer);
      // If by now the outbox is empty + nothing in flight, declare
      // the burst settled and the bottom banner disappears.
      setRecentEdits((c) => {
        const queue = (health?.buckets?.pending || 0) + (health?.buckets?.in_flight || 0);
        return queue > 0 ? c : 0;
      });
    }, 30000);
    return () => { clearInterval(fastTimer); clearTimeout(settle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEditAt]);

  // Clear the "recent edits" badge as soon as the queue is empty
  // post-burst — i.e. the user-initiated change has flushed to WP.
  useEffect(() => {
    if (!health || !recentEdits) return;
    const queue = (health.buckets?.pending || 0) + (health.buckets?.in_flight || 0);
    if (queue === 0 && Date.now() - lastEditAt > 3000) {
      setRecentEdits(0);
    }
  }, [health, recentEdits, lastEditAt]);

  const triggerPull = useCallback(async (fullRefresh) => {
    if (!orgId) return;
    setPulling(fullRefresh ? 'full' : 'incr');
    setPullResult(null);
    try {
      const url = `${API}/api/wp-sync/${orgId}/pull${fullRefresh ? '?full_refresh=1' : ''}`;
      const { data } = await axios.post(url, null, { withCredentials: true });
      // Summarise: total rows pulled / applied across entities.
      const entries = Array.isArray(data) ? data : (data?.entities || []);
      const totals = entries.reduce((acc, e) => {
        acc.pulled   += e.pulled   || 0;
        acc.applied  += e.applied  || 0;
        acc.adopted  += e.adopted  || 0;
        acc.seeded   += e.seeded   || 0;
        acc.unchanged += e.unchanged || 0;
        acc.skipped  += e.skipped_out_of_window || 0;
        acc.conflicts += e.conflicts || 0;
        acc.errors   += e.ok === false ? 1 : 0;
        return acc;
      }, { pulled: 0, applied: 0, adopted: 0, seeded: 0, unchanged: 0, skipped: 0, conflicts: 0, errors: 0 });
      setPullResult({ ok: totals.errors === 0, ...totals,
                      kind: fullRefresh ? 'full' : 'incremental' });
    } catch (e) {
      setPullResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setPulling(null);
      // Re-fetch both views so timestamps + queue update immediately.
      fetchAll({ silent: true });
    }
  }, [orgId, fetchAll]);

  /* Force full resync (OL → WP). Re-pushes every flagged doc — plus, with
     force=1, re-projects every in-scope row regardless of the timestamp
     comparison — through the same push_org() the cron uses. Use when WP is
     wrong / after a stall. The current-season guard still bounds it. */
  const triggerPush = useCallback(async () => {
    if (!orgId || pushing) return;
    setPushing(true);
    setPushResult(null);
    try {
      const { data } = await axios.post(
        `${API}/api/wp-sync/${orgId}/resync-all`, null, { withCredentials: true },
      );
      if (data?.ok !== false) {
        setPushResult({ ok: true, started: true });
        // Kick fast polling so the pill visibly moves as the background job drains.
        setLastEditAt(Date.now());
      } else {
        setPushResult({ ok: false, error: data.error || 'resync failed' });
      }
    } catch (e) {
      setPushResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setPushing(false);
      fetchAll({ silent: true });
    }
  }, [orgId, pushing, fetchAll]);

  /* Clear Sync — quarantine docs stuck in wp_sync_state=error (they retry and
     fail forever) to the non-syncing "local" state and cancel dead-letter queue
     rows, so the pill can return to green. Reversible: Force full resync
     re-attempts every row regardless of state. */
  const triggerClear = useCallback(async () => {
    if (!orgId || clearing) return;
    setClearing(true);
    setClearResult(null);
    try {
      const { data } = await axios.post(
        `${API}/api/wp-sync/${orgId}/clear-errors`, null, { withCredentials: true },
      );
      setClearResult({ ok: true, cleared: data.cleared_total || 0, dead: data.cancelled_dead_rows || 0, drift: data.cleared_drift_total || 0 });
    } catch (e) {
      setClearResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setClearing(false);
      fetchAll({ silent: true });
    }
  }, [orgId, clearing, fetchAll]);

  /* Next-cron-tick estimate. We don't have a server-published "next
     scheduled at" yet, so we approximate from the freshest watermark:
     the agent ticks every 15 min, so the next tick is bounded by
     `latest_pull + 15 min`. Good enough as a status hint. */
  const nextSweepAt = useMemo(() => {
    if (!agent?.entities?.length) return null;
    const latest = agent.entities
      .map(e => e.last_pulled_at && Date.parse(e.last_pulled_at))
      .filter(Boolean)
      .reduce((a, b) => Math.max(a, b), 0);
    if (!latest) return null;
    return new Date(latest + CRON_INTERVAL_MS);
  }, [agent]);

  if (loading || !health) {
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-700/40 text-slate-400 text-xs" data-testid="sync-health-pill-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        WP sync…
      </div>
    );
  }
  if (err) {
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-900/30 text-red-300 text-xs border border-red-700/40" title={err}>
        <AlertTriangle className="h-3.5 w-3.5" />
        Sync probe failed
      </div>
    );
  }

  // Effective tone: an in-flight edit-burst forces amber over the
  // backend-reported tone so the user sees "syncing" instantly. Once
  // the queue drains AND recentEdits is reset, we revert to whatever
  // /sync-health reports.
  const effectiveTone = recentEdits > 0 ? 'amber' : (health.tone || 'green');
  const tone   = TONE_STYLES[effectiveTone] || TONE_STYLES.green;
  const Icon   = tone.Icon;
  const total  = (health.buckets.pending || 0) + (health.buckets.in_flight || 0) + (health.buckets.done || 0) + (health.buckets.dead || 0);
  const lastTs = health.last_event?.enqueued_at
    ? new Date(health.last_event.enqueued_at).toLocaleTimeString()
    : null;

  return (
    <>
    <div className="relative" data-testid="sync-health-pill">
      <button
        onClick={() => setOpen((v) => { const next = !v; if (next) fetchAll({ silent: true }); return next; })}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition ${tone.pill}`}
        data-testid="sync-health-pill-btn"
        title={`WP Sync · ${effectiveTone === 'amber' && recentEdits > 0 ? `Syncing ${recentEdits} change(s)…` : health.label} · click for status + resync`}
      >
        <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
        <span>WP Sync</span>
        {effectiveTone === 'red' && (
          <span className="ml-0.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500/30 text-red-100 text-[10px] font-semibold">
            {health.buckets.dead + (health.stuck_errors || 0)}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1.5 w-[22rem] z-50 rounded-lg shadow-xl bg-slate-900 border border-slate-700 p-3 text-xs text-slate-200"
          onMouseLeave={() => setOpen(false)}
          data-testid="sync-health-pill-popover"
        >
          {/* ── Section 1: Outbox health (OL → WP) ───────────────────── */}
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold flex items-center gap-1.5">
              <Icon className="h-3.5 w-3.5" /> {health.label}
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); fetchAll(); }}
              className="text-slate-400 hover:text-slate-100"
              data-testid="sync-health-refresh"
              title="Refresh status"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="text-slate-400 text-[11px] mb-2">
            Last hour · {total} replication event{total === 1 ? '' : 's'}
          </p>
          <div className="grid grid-cols-2 gap-1.5 mb-2">
            <PillRow label="✓ Done" value={health.buckets.done}      tone="emerald" />
            <PillRow label="↻ Pending" value={health.buckets.pending}    tone="amber"   />
            <PillRow label="↗ In-flight" value={health.buckets.in_flight} tone="amber"   />
            <PillRow label="✗ Dead-letter" value={health.buckets.dead}    tone={health.buckets.dead > 0 ? 'red' : 'slate'} />
          </div>

          {health.sweep_stale && (
            <div className="border-t border-red-800/50 pt-2 mb-2">
              <p className="text-red-300 font-medium" data-testid="sync-health-stalled">
                ⛔ Sync agent has not swept recently{health.last_sweep_at ? ` (last ${timeAgo(health.last_sweep_at)})` : ''}. It may be stalled — pushes/pulls are not running.
              </p>
            </div>
          )}

          {(health.stuck_errors > 0 || health.pending_docs > 0) && (
            <div className="border-t border-slate-700 pt-2 space-y-1 mb-2">
              {health.stuck_errors > 0 && (
                <p className="text-amber-200/80" data-testid="sync-health-stuck-errors">
                  ⚠ {health.stuck_errors} doc{health.stuck_errors === 1 ? '' : 's'} stuck in <code>wp_sync_state=error</code> — will retry on the next sweep
                </p>
              )}
              {health.pending_docs > 0 && (
                <p className="text-amber-200/80" data-testid="sync-health-pending-docs">
                  {health.pending_docs.toLocaleString()} doc{health.pending_docs === 1 ? '' : 's'} pending WP push — will reconcile on the next sweep
                </p>
              )}
            </div>
          )}

          {health.last_event && (
            <p className="text-slate-500 text-[10px] mb-3 pb-2 border-b border-slate-800">
              Last event: <span className="text-slate-300">{health.last_event.method} {health.last_event.entity}</span> · {health.last_event.status} · {lastTs}
            </p>
          )}

          {/* ── Section 2: Per-entity WP→OL freshness ─────────────────── */}
          <div className="mb-2 flex items-center justify-between">
            <p className="font-semibold flex items-center gap-1.5">
              <Database className="h-3.5 w-3.5" /> Grid freshness
            </p>
            {nextSweepAt && (
              <span className="text-slate-500 text-[10px]" data-testid="sync-health-next-sweep">
                next auto-sweep ≈ {timeAgo(nextSweepAt.toISOString()).replace('ago', '').trim() || 'soon'}
                {nextSweepAt.getTime() < Date.now() ? ' (overdue)' : ''}
              </span>
            )}
          </div>

          {agent?.entities?.length ? (
            <div className="max-h-48 overflow-y-auto pr-1 mb-2 space-y-0.5" data-testid="sync-health-entities">
              {agent.entities
                // Show entities with watermarks first, then never-pulled.
                .slice()
                .sort((a, b) => {
                  if (!a.last_pulled_at && b.last_pulled_at) return 1;
                  if (a.last_pulled_at && !b.last_pulled_at) return -1;
                  return (b.last_pulled_at || '').localeCompare(a.last_pulled_at || '');
                })
                .map((e) => {
                  const driftBad  = e.drift_rows > 0 || e.wp_overwrote_ol > 0;
                  return (
                    <div
                      key={e.entity}
                      className={`flex items-center justify-between gap-2 px-2 py-1 rounded text-[11px] ${driftBad ? 'bg-amber-500/10' : 'bg-slate-800/40'}`}
                      data-testid={`sync-entity-${e.entity}`}
                    >
                      <span className="text-slate-200 truncate">
                        {ENTITY_LABELS[e.entity] || e.entity}
                      </span>
                      <span className="flex items-center gap-2 text-slate-400 shrink-0">
                        {driftBad && (
                          <span className="text-amber-300" title={`${e.drift_rows} drift · ${e.wp_overwrote_ol} WP→OL · ${e.ol_newer} OL→WP`}>
                            ⚠ {e.drift_rows}
                          </span>
                        )}
                        <span className="font-mono" title={e.last_pulled_at || ''}>
                          {timeAgo(e.last_pulled_at)}
                        </span>
                      </span>
                    </div>
                  );
                })}
            </div>
          ) : (
            <p className="text-slate-500 text-[10px] mb-2 italic">
              {agent?.totals ? 'No watermarks yet — first pull pending.' : 'Agent status unavailable for this role.'}
            </p>
          )}

          {/* ── Section 3: On-demand pull ─────────────────────────────── */}
          <div className="border-t border-slate-700 pt-2 flex items-center gap-1.5">
            <button
              onClick={(ev) => { ev.stopPropagation(); triggerPull(false); }}
              disabled={!!pulling}
              className="flex-1 flex items-center justify-center gap-1.5 h-7 rounded bg-emerald-600/80 hover:bg-emerald-600 disabled:opacity-50 text-white text-[11px] font-medium transition"
              data-testid="sync-pull-incremental-btn"
              title="Incremental pull — uses watermarks for cheap delta-only fetch (fast)"
            >
              {pulling === 'incr'
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Download className="h-3 w-3" />}
              Pull from WP
            </button>
            <button
              onClick={(ev) => { ev.stopPropagation(); triggerPull(true); }}
              disabled={!!pulling}
              className="flex items-center justify-center gap-1 h-7 px-2 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-100 text-[11px] font-medium transition"
              data-testid="sync-pull-full-btn"
              title="Full refresh — discards watermarks for this cycle, pulls every row. Heavier; use sparingly."
            >
              {pulling === 'full'
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <FastForward className="h-3 w-3" />}
              Full
            </button>
          </div>

          {/* ── Section 4: On-demand push (OL → WP) ───────────────────── */}
          <div className="border-t border-slate-700 pt-2 mt-2 space-y-1.5">
            <button
              onClick={(ev) => { ev.stopPropagation(); triggerClear(); }}
              disabled={clearing || (health.stuck_errors === 0 && health.buckets.dead === 0 && (health.drift_docs || 0) === 0)}
              className="w-full flex items-center justify-center gap-1.5 h-7 rounded bg-amber-600/80 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[11px] font-medium transition"
              data-testid="sync-clear-errors-btn"
              title="Clear Sync — quarantine docs stuck in wp_sync_state=error, cancel dead-letter rows, and clear informational drift flags on healthy rows so the pill can return to green. Reversible: Force full resync re-attempts them. Disabled when nothing is stuck or drifting."
            >
              {clearing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eraser className="h-3 w-3" />}
              {clearing
                ? 'Clearing…'
                : (health.stuck_errors === 0 && health.buckets.dead === 0 && (health.drift_docs || 0) === 0)
                  ? 'Clear Sync — all clear'
                  : `Clear Sync (${health.stuck_errors + health.buckets.dead + (health.drift_docs || 0)})`}
            </button>
            {clearResult && (
              <div
                className={`text-[10px] ${clearResult.ok ? 'text-emerald-300' : 'text-red-300'}`}
                data-testid="sync-clear-result"
              >
                {clearResult.ok
                  ? `✓ Cleared ${clearResult.cleared} stuck doc(s)${clearResult.dead ? ` + ${clearResult.dead} dead-letter row(s)` : ''}${clearResult.drift ? ` + ${clearResult.drift} drift flag(s)` : ''} — pill should turn green.`
                  : `✗ clear failed${clearResult.error ? ` — ${clearResult.error}` : ''}`}
              </div>
            )}
            <button
              onClick={(ev) => { ev.stopPropagation(); triggerPush(); }}
              disabled={pushing}
              className="w-full flex items-center justify-center gap-1.5 h-7 rounded bg-indigo-600/80 hover:bg-indigo-600 disabled:opacity-50 text-white text-[11px] font-medium transition"
              data-testid="sync-push-force-btn"
              title="Force full resync — re-pushes every flagged doc, re-projects every in-season row, AND reconciles game scores from OL to WordPress now. Use after a stall or when WP looks wrong."
            >
              {pushing
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <UploadCloud className="h-3 w-3" />}
              {pushing ? 'Resyncing OL → WP…' : 'Force full resync (incl. scores)'}
            </button>
            {pushResult && (
              <div
                className={`text-[10px] mt-1.5 ${pushResult.ok ? 'text-emerald-300' : 'text-red-300'}`}
                data-testid="sync-push-result"
              >
                {pushResult.ok ? (
                  <p className="font-medium" data-testid="sync-push-breakdown">
                    ✓ Full resync started (incl. scores) — the pill will update as it runs.
                  </p>
                ) : (
                  <p>✗ resync failed{pushResult.error ? ` — ${pushResult.error}` : ''}</p>
                )}
              </div>
            )}
          </div>

          {pullResult && (
            <div
              className={`text-[10px] mt-1.5 ${pullResult.ok ? 'text-emerald-300' : 'text-red-300'}`}
              data-testid="sync-pull-result"
            >
              {pullResult.ok ? (
                <>
                  <p className="font-medium">✓ {pullResult.kind} pull complete</p>
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-slate-300" data-testid="sync-pull-breakdown">
                    <span title="Rows examined from WP">scanned {pullResult.pulled}</span>
                    <span className="text-emerald-300" title="Existing OL rows updated with changed fields">· updated {pullResult.applied}</span>
                    <span className="text-sky-300" title="Rows re-linked to the connected WP by natural key (no duplicate created)">· linked {pullResult.adopted}</span>
                    <span className="text-amber-300" title="Brand-new in-scope rows imported from WP">· imported {pullResult.seeded}</span>
                    <span className="text-slate-400" title="Already-in-sync rows (nothing rewritten)">· unchanged {pullResult.unchanged}</span>
                    {pullResult.skipped > 0 && <span className="text-slate-400" title="Out-of-season WP rows deliberately NOT seeded (delta guardrail)">· skipped {pullResult.skipped}</span>}
                    {pullResult.conflicts > 0 && <span className="text-amber-300" title="WP overwrote a locally-edited OL row (drift)">· ⚠ drift {pullResult.conflicts}</span>}
                  </div>
                </>
              ) : (
                <p>✗ pull failed{pullResult.error ? ` — ${pullResult.error}` : ''}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>

    {/* Floating bottom banner — visible while a recent edit burst is
        still draining to WP. Keeps the user informed even when the
        pill is off-screen (long scrolling grids). Auto-clears once
        the outbox queue is empty and the 30s cool-down has elapsed. */}
    {recentEdits > 0 && (
      <div
        className="fixed bottom-4 right-4 z-[60] flex items-center gap-2.5 px-3.5 py-2 rounded-full bg-amber-500/15 border border-amber-500/40 backdrop-blur shadow-xl text-amber-100 text-xs"
        data-testid="sync-floating-banner"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Syncing {recentEdits} change{recentEdits === 1 ? '' : 's'} to WordPress…</span>
      </div>
    )}
    </>
  );
}

function PillRow({ label, value, tone }) {
  const toneCls = {
    emerald: 'text-emerald-300',
    amber:   'text-amber-300',
    red:     'text-red-300',
    slate:   'text-slate-400',
  }[tone] || 'text-slate-400';
  return (
    <div className="flex items-center justify-between bg-slate-800/60 rounded px-2 py-1">
      <span className={`text-[10px] ${toneCls}`}>{label}</span>
      <span className={`font-mono ${toneCls}`}>{value ?? 0}</span>
    </div>
  );
}
